import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead, { SITE_URL, SITE_NAME } from '../components/SEOHead';
import './BlogDetail.css';

// react-snap prerenders with userAgent "ReactSnap", using an old bundled
// Chromium that can crash parsing modern syntax in third-party embed scripts
// (confirmed with Google Maps' embed script — see ContactMap.js). YouTube's
// embed player is the same class of risk and untested against it, so skip it
// during prerender too; real visitors still get the live embed on hydration.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/media/'}${path}`;
};

const formatBlogDate = (dateStr, language) => {
  try {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const isKa = String(language || '').toLowerCase().startsWith('ka');
    if (isKa) {
      const kaMonths = [
        'იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი',
        'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი',
      ];
      return `${kaMonths[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return dateStr;
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const photosGridRef = useRef(null);
  const isPhotoHoveredRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/content/blogs/${slug}/`);
        setItem(res.data);
      } catch (e) {
        setError(i18n.language === 'ka' ? 'ბლოგის ჩატვირთვა ვერ მოხერხდა' : 'Failed to load blog');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, i18n.language]);

  const localized = useMemo(() => {
    if (!item) return null;
    const isKa = String(i18n.language || '').toLowerCase().startsWith('ka');
    const title = (isKa ? item.title_ka || item.title_en : item.title_en || item.title_ka) || '';
    const longDesc =
      (isKa ? item.long_description_ka || item.long_description_en : item.long_description_en || item.long_description_ka) ||
      '';
    const short = (isKa ? item.short_description_ka || item.short_description_en : item.short_description_en || item.short_description_ka) || '';
    const seoTitle = (isKa ? item.meta_title_ka || item.meta_title_en : item.meta_title_en || item.meta_title_ka) || '';
    const seoDescription = (isKa ? item.meta_description_ka || item.meta_description_en : item.meta_description_en || item.meta_description_ka) || '';

    const mainPhotoUrl = getImageUrl(item.main_photo);
    const detailHeaderUrl = getImageUrl(item.detail_header_photo);
    const galleryPhotos = (item.secondary_photos || [])
      .map((p) => ({ url: getImageUrl(p.photo) }))
      .filter((p) => p.url);

    return {
      title,
      short,
      longDesc,
      seoTitle,
      seoDescription,
      dateLabel: formatBlogDate(item.date, i18n.language),
      heroUrl: detailHeaderUrl || mainPhotoUrl,
      youtubeEmbedUrl: item.youtube_embed_url,
      galleryPhotos,
    };
  }, [item, i18n.language]);

  const galleryPhotos = useMemo(() => localized?.galleryPhotos ?? [], [localized]);
  const renderedPhotos = useMemo(() => {
    if (galleryPhotos.length <= 1) return galleryPhotos;
    return [...galleryPhotos, ...galleryPhotos];
  }, [galleryPhotos]);

  // Reset auto-scroll when blog changes
  useEffect(() => {
    if (photosGridRef.current) {
      photosGridRef.current.scrollTop = 0;
    }
  }, [slug]);

  useEffect(() => {
    // Ensure first visible state is always anchored to photo 1.
    if (!photosGridRef.current) return;
    photosGridRef.current.scrollTop = 0;
  }, [renderedPhotos.length]);

  // Continuous vertical auto-scroll loop (no index stepping/jumps)
  useEffect(() => {
    if (renderedPhotos.length <= 1 || !photosGridRef.current) return undefined;
    const container = photosGridRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduceMotion) return undefined;

    let rafId = 0;
    let lastTs = 0;
    const speedPxPerSecond = 20;

    const tick = (ts) => {
      if (!container) return;
      if (!lastTs) {
        lastTs = ts;
        rafId = window.requestAnimationFrame(tick);
        return;
      }
      const delta = ts - lastTs;
      lastTs = ts;
      if (isPhotoHoveredRef.current) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      // On phones the grid is laid out in the normal page flow (nothing to auto-scroll),
      // so the page itself scrolls through the photos.
      if (container.scrollHeight <= container.clientHeight + 1) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      const loopHeight = container.scrollHeight / 2;
      if (loopHeight <= 1) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      let nextTop = container.scrollTop + (speedPxPerSecond * delta) / 1000;
      if (nextTop >= loopHeight) {
        nextTop -= loopHeight;
      }
      container.scrollTop = nextTop;
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [renderedPhotos.length]);

  if (loading) {
    return <div className="blog-detail-state">{i18n.language === 'ka' ? 'იტვირთება...' : 'Loading...'}</div>;
  }

  if (error || !localized) {
    return <div className="blog-detail-state">{error || (i18n.language === 'ka' ? 'ვერ მოიძებნა' : 'Not found')}</div>;
  }

  const descriptionLabel = i18n.language === 'ka' ? 'აღწერა' : 'Description';
  const photosLabel = i18n.language === 'ka' ? 'ფოტოები' : 'Photos';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: localized.title,
      description: (localized.short || localized.longDesc).slice(0, 155),
      image: item.main_photo || undefined,
      datePublished: item.date || undefined,
      dateModified: item.updated_at || item.date || undefined,
      author: { '@type': 'Organization', name: SITE_NAME },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      mainEntityOfPage: `${SITE_URL}/history/blogs/${slug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Blog', item: `${SITE_URL}/history/blogs` },
        { '@type': 'ListItem', position: 2, name: localized.title, item: `${SITE_URL}/history/blogs/${slug}` },
      ],
    },
  ];

  return (
    <div className="blog-detail-page">
      <SEOHead
        title={localized.seoTitle || `${localized.title} | GFM Studio Blog`}
        description={localized.seoDescription || (localized.short || localized.longDesc).slice(0, 155)}
        path={`/history/blogs/${slug}`}
        image={item.og_image || item.main_photo || undefined}
        type="article"
        noindex={item.robots_index === false}
        jsonLd={jsonLd}
      />
      <section className="blog-detail-hero">
        <div className="blog-detail-hero-media">
          {localized.heroUrl ? (
            <img src={localized.heroUrl} alt={localized.title} />
          ) : (
            <div className="blog-detail-hero-placeholder" />
          )}
          <div className="blog-detail-hero-overlay" />
          <div className="blog-detail-hero-content">
            <h1 className="blog-detail-hero-title">{localized.title}</h1>
            <div className="blog-detail-hero-date">{localized.dateLabel}</div>
          </div>
        </div>
      </section>

      <div className="blog-detail-body">
        <div className="blog-detail-grid">
          <article className="blog-detail-article">
            <h2 className="blog-detail-section-title">{descriptionLabel}</h2>
            <div className="blog-detail-main-scroll">
              {localized.longDesc ? (
                <div className="blog-detail-description">
                  {String(localized.longDesc)
                    .split('\n')
                    .map((p, idx) => (p.trim() ? <p key={idx}>{p}</p> : null))}
                </div>
              ) : (
                <div className="blog-detail-empty">
                  {i18n.language === 'ka' ? 'აღწერა მალე დაემატება' : 'Description coming soon'}
                </div>
              )}

              {localized.youtubeEmbedUrl && !isPrerender ? (
                <div className="blog-detail-video">
                  <iframe
                    src={localized.youtubeEmbedUrl}
                    title={`${localized.title} video`}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </div>
          </article>

          <aside className="blog-detail-aside">
            <h2 className="blog-detail-section-title blog-detail-photos-title">{photosLabel}</h2>
            {galleryPhotos.length ? (
              <div
                ref={photosGridRef}
                className="blog-detail-photo-grid"
                onMouseEnter={() => {
                  isPhotoHoveredRef.current = true;
                }}
                onMouseLeave={() => {
                  isPhotoHoveredRef.current = false;
                }}
              >
                {renderedPhotos.map((p, idx) => {
                  // The second copy only exists to make the desktop auto-scroll loop seamless.
                  const isLoopCopy = idx >= galleryPhotos.length;
                  return (
                    <div
                      key={`${idx}-${p.url}`}
                      className={`blog-detail-photo${isLoopCopy ? ' blog-detail-photo--loop-copy' : ''}`}
                      aria-hidden={isLoopCopy ? 'true' : undefined}
                    >
                      <img src={p.url} alt={isLoopCopy ? '' : `${localized.title} — ${idx + 1}`} loading="lazy" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="blog-detail-empty">{i18n.language === 'ka' ? 'ფოტოები მალე დაემატება' : 'Photos coming soon'}</div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;

